import { night1 } from './night1';
import type { LevelDef } from './types';

/**
 * Day 3 — Wednesday, after cram school. The same walk from Yoruno Station as
 * Day 1, but after dark, and someone else gets off the train. The first night
 * he follows her, on streets she already knows: stay under the lights down the
 * station road, then (past the cross street) the lights stop helping, and near
 * home the dark side streets are the safer way.
 */
export const station: LevelDef = {
  id: 'station',
  night: 3,
  title: 'Station Road',
  subtitle: 'Wednesday · 22:40',
  look: 'school',
  card: ['Cram school ran late.', 'The last train but one, then the walk from Yoruno Station.', 'Someone else got off at your stop.'],
  clockStart: 22 * 60 + 40,
  // The same streets as Day 1.
  map: night1.map,
  zones: [
    { r0: 21, zone: 1 },
    { r0: 11, r1: 20, zone: 2 },
    { r1: 10, zone: 3 },
  ],
  battery: 30,
  // The first night he follows: a little gentler than the park.
  pace: { tension: 0.55, patience: 480 },
  wind: 0.2,
  theme: {
    sky: '#05080f',
    fog: { color: '#0a0f18', density: 0.03 },
    hemi: { sky: '#4a5c86', ground: '#0b0a08', intensity: 0.8 },
    ambient: 0.5,
    moon: 0.6,
  },
  stranger: { walkMax: 2.7, chaseSpeed: 2.95, gapMax: 42, spawnDelay: 8 },
  cat: { ...night1.cat!, line: 'cat' },
  lines: {
    intro: 'Same way as always. Five minutes from the station.',
    firstSight: 'Someone else got off the train. He\'s going my way.',
    konbini: 'The konbini\'s still open. I could wait inside… he\'d just wait too.',
    dark1: 'This street is so quiet at night. I never noticed.',
    act2: 'Every streetlight… he can see exactly where I am.',
    act3: 'The side streets are dark. If I can\'t see him, maybe he can\'t see me.',
    roadworks: 'The road works. I forgot. I have to go back — towards him.',
    deadend: 'Dead end. Not now. Not now.',
    cat: 'Just the cat. Just the cat…',
    path: 'The shrine path. Dark, but it cuts straight through.',
    pathExitGone: 'Out the other side. Is he still back there?',
    wrongBuilding: 'Maison Sakura. No — mine\'s the next one. Keep going.',
    homeStretch: 'My building. Keys. Keys, keys, keys.',
  },
  endings: {
    home: {
      title: 'Home.',
      lines: ['She doesn\'t turn the light on.', 'From the window, the street below is empty.', 'Whoever he was, he got off at her stop.'],
      hint: 'Next: Thursday. The long way home, through the park.',
    },
  },
  mom: {
    first: { node: 'start', at: 7 },
    script: {
      start: {
        text: 'Finished at cram school? Want me to come meet you at the station?',
        options: [
          { text: "No it's fine, walking now", end: 'Ok. Straight home.' },
          { text: 'Maybe…', next: 'meet' },
        ],
      },
      meet: {
        text: "I'm still in my pyjamas 😅 Are you ok?",
        options: [
          { text: "I'm fine, nevermind", end: 'Text me when you get in.' },
          { text: 'Some guy got off the train with me', next: 'guy' },
        ],
      },
      guy: {
        text: 'Stay on the main road where it is bright. Call me if anything happens.',
        options: [
          { text: 'Ok', end: "I'm watching for you from the window." },
          { text: "I'm probably overthinking it", end: 'Maybe. Stay where it is bright anyway.' },
        ],
      },
      act2: {
        text: 'Where are you now?',
        options: [
          { text: 'Halfway', end: 'Ok ❤️' },
          { text: 'He is still behind me', next: 'behind' },
        ],
      },
      behind: {
        text: "Don't look at him. Just keep walking. I'll come down to the door.",
        options: [
          { text: 'Ok', end: 'Nearly home.' },
          { text: 'Hurry', end: 'Coming.' },
        ],
      },
    },
  },
  onEnterCell(ctx, cell) {
    const key = `${cell.c},${cell.r}`;
    if (key === '10,22' || key === '10,23') ctx.sayOnce('konbini');
    if (key === '10,26') ctx.sayOnce('dark1');
    if (key === '10,13') ctx.sayOnce('roadworks');
    if (key === '17,14') ctx.sayOnce('deadend');
    if (cell.r === 20 && !ctx.said('act2mom')) {
      ctx.sayOnce('act2mom');
      ctx.momLater('act2', 6);
    }
    if (cell.r === 2) ctx.sayOnce('homeStretch');
  },
  onZoneChange(ctx, zone) {
    if (zone === 2) {
      ctx.sayOnce('act2');
      ctx.notify('yui3w', 'Yui', 'omg did u see the news?? that was YOUR station 😨', 'normal', 9);
    }
    if (zone === 3) ctx.sayOnce('act3');
  },
};
